import { Form, Head, Link, usePage } from '@inertiajs/react'
import { GitHubLogoIcon } from '@radix-ui/react-icons'

import { DashboardTabs } from '@/components/dashboard/dashboard-tabs'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DashboardShell } from '@/layouts/dashboard-shell'
import { cn } from '@/lib/utils'
import {
  organizationRepositoryPath,
  organizationSettingsPath,
  organizationSlackConnectPath,
  organizationSlackPath,
} from '@/routes'
import type { OrganizationsDashboardShow } from '@/types'

function SlackMark({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className ?? 'mr-3 h-5 w-5'}
      viewBox="0 0 122.8 122.8"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M25.8 77.6c0 7.1-5.8 12.9-12.9 12.9S0 84.7 0 77.6s5.8-12.9 12.9-12.9h12.9v12.9zm6.5 0c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9v32.3c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V77.6z"
        fill="#e01e5a"
      />
      <path
        d="M45.2 25.8c-7.1 0-12.9-5.8-12.9-12.9S38.1 0 45.2 0s12.9 5.8 12.9 12.9v12.9H45.2zm0 6.5c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H12.9C5.8 58.1 0 52.3 0 45.2s5.8-12.9 12.9-12.9h32.3z"
        fill="#36c5f0"
      />
      <path
        d="M97 45.2c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9-5.8 12.9-12.9 12.9H97V45.2zm-6.5 0c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V12.9C64.7 5.8 70.5 0 77.6 0s12.9 5.8 12.9 12.9v32.3z"
        fill="#2eb67d"
      />
      <path
        d="M77.6 97c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9-12.9-5.8-12.9-12.9V97h12.9zm0-6.5c-7.1 0-12.9-5.8-12.9-12.9s5.8-12.9 12.9-12.9h32.3c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H77.6z"
        fill="#ecb22e"
      />
    </svg>
  )
}

export default function OrganizationDashboardShow({
  organization,
  repositories,
  slackIntegration,
  nextEmailAt,
  nextSlackAt,
  isAdmin,
  emailDigestEnabled,
}: OrganizationsDashboardShow) {
  const { analyticsEnabled } = usePage().props
  const orgId = organization.id

  return (
    <>
      <Head title={`${organization.name} — Dashboard`} />
      <DashboardShell organization={organization} repositories={repositories} hideTitle>
        <h1 className="sr-only">Dashboard overview</h1>
        <DashboardTabs organizationId={orgId} active="overview" analyticsEnabled={analyticsEnabled} />

        <div className="mx-auto w-full max-w-2xl space-y-6">
          {repositories.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Repositories</CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className={cn(
                    'grid gap-4',
                    repositories.length === 1 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2',
                  )}
                >
                  {repositories.map((repo) => (
                    <div
                      key={repo.id}
                      className="bg-muted rounded-lg p-4 shadow-xs transition-shadow hover:shadow-md"
                    >
                      <Link
                        href={organizationRepositoryPath(orgId, repo.owner, repo.name)}
                        className="text-foreground focus-visible:ring-ring flex min-h-11 items-center rounded-sm hover:underline focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                      >
                        <GitHubLogoIcon className="mr-2 size-4 shrink-0" aria-hidden="true" />
                        <span className="truncate font-medium">{repo.fullName}</span>
                      </Link>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : null}

          {emailDigestEnabled ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Upcoming Digest</CardTitle>
              </CardHeader>
              <CardContent className="text-muted-foreground text-sm">
                Next email scheduled for: <span className="text-foreground font-medium">{nextEmailAt}</span>
                <span className="text-muted-foreground ml-2 capitalize">({organization.digestFrequency})</span>
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Integrations</CardTitle>
            </CardHeader>
            <CardContent>
              {slackIntegration ? (
                <div className="space-y-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex min-w-0 items-start gap-3">
                      <SlackMark className="mt-0.5 h-5 w-5 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-base leading-snug font-semibold">
                          Connected to {slackIntegration.teamName}
                        </p>
                        <p className="text-muted-foreground mt-0.5 text-sm">#{slackIntegration.channelName}</p>
                      </div>
                    </div>
                    {isAdmin ? (
                      <Form method="delete" action={organizationSlackPath(orgId)} className="shrink-0">
                        {({ processing }) => (
                          <Button type="submit" variant="outline" className="min-h-11" disabled={processing}>
                            {processing ? 'Disconnecting…' : 'Disconnect Slack'}
                          </Button>
                        )}
                      </Form>
                    ) : null}
                  </div>
                  {nextSlackAt ? (
                    <p className="text-muted-foreground text-sm">
                      Next Slack send: <span className="text-foreground font-medium">{nextSlackAt}</span>
                    </p>
                  ) : null}
                </div>
              ) : (
                <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                  <p className="text-muted-foreground text-sm">Connect Slack to receive digests in a channel.</p>
                  {isAdmin ? (
                    <Button asChild className="min-h-11">
                      <a href={organizationSlackConnectPath(orgId)}>
                        <SlackMark className="mr-2 h-4 w-4" />
                        Connect Slack
                      </a>
                    </Button>
                  ) : null}
                </div>
              )}
            </CardContent>
          </Card>

          {isAdmin ? (
            <p className="text-muted-foreground text-center text-sm">
              Manage repos and digest timing in{' '}
              <Link href={organizationSettingsPath(orgId)} className="text-foreground underline">
                Settings
              </Link>
              .
            </p>
          ) : null}
        </div>
      </DashboardShell>
    </>
  )
}
